import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CartProvider, useCart } from './context/CartContext';
import { SelectionProvider } from './context/SelectionContext';
import { OrderLimitsProvider } from './context/OrderLimitsContext';
import { ModuleConfigProvider, useModuleConfig } from './context/ModuleConfigContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useUserPermissions } from './hooks/useUserPermissions';
import Header from './components/Header';
import Footer from './components/Footer';
import LoginPage from './components/LoginPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import SetNewAccountPasswordPage from './components/SetNewAccountPasswordPage';
import PasswordResetRequestPage from './components/PasswordResetRequestPage';
import AccountRequestPage from './components/AccountRequestPage';
import CustomMatsPage from './pages/CustomMatsPage';
import UVKitsPage from './pages/UVKitsPage';
import ProductsPage from './pages/ProductsPage';
import UserAccountPage from './pages/UserAccountPage';
import OrderHistoryPage from './pages/OrderHistoryPage';
import ContentPage from './pages/ContentPage';
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/index.css';

function AppContent() {
  const { activeModule, isLoading, error } = useModuleConfig();
  const { isAuthenticated, isCheckingAuth, logout } = useAuth();
  const { calculatePrices } = useCart();
  const permissions = useUserPermissions();

  // Calculate prices (including credit limit) when user logs in
  useEffect(() => {
    if (isAuthenticated && !isCheckingAuth) {
      console.log('User authenticated, fetching credit limit...');
      calculatePrices();
    }
  }, [isAuthenticated, isCheckingAuth]);

  // Check for password reset token - logout if authenticated
  const urlParams = new URLSearchParams(window.location.search);
  const resetToken = urlParams.get('token');

  useEffect(() => {
    // If there's a reset token and user is authenticated, logout first (without redirect)
    if (resetToken && isAuthenticated && !isCheckingAuth) {
      console.log('Password reset token detected, logging out user...');
      logout(true); // skipRedirect = true to keep the URL with token
    }
  }, [resetToken, isAuthenticated, isCheckingAuth, logout]);

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="App">
        <Header />
        <div className="container py-4">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show appropriate page based on path (after logout if needed when token present)
  const currentPath = window.location.pathname;

  if (!isAuthenticated) {
    // New account setup flow (with token)
    if (currentPath === '/account-instellen' && resetToken) {
      return (
        <div className="App">
          <Header />
          <SetNewAccountPasswordPage />
          <Footer />
        </div>
      );
    }

    // Password reset flow (with token)
    if (currentPath === '/wachtwoord-instellen' && resetToken) {
      return (
        <div className="App">
          <Header />
          <ResetPasswordPage />
          <Footer />
        </div>
      );
    }

    // Account request page
    if (currentPath === '/account-aanvragen') {
      return (
        <div className="App">
          <Header />
          <AccountRequestPage />
          <Footer />
        </div>
      );
    }

    // Password reset request page
    if (currentPath === '/wachtwoord-vergeten') {
      return (
        <div className="App">
          <Header />
          <PasswordResetRequestPage />
          <Footer />
        </div>
      );
    }

    // Allow public access to contact and terms pages
    if (currentPath === '/contact') {
      return (
        <div className="App">
          <Header />
          <ContentPage contentKey="content.contact" />
          <Footer />
        </div>
      );
    }

    if (currentPath === '/terms') {
      return (
        <div className="App">
          <Header />
          <ContentPage contentKey="content.terms" />
          <Footer />
        </div>
      );
    }

    return (
      <div className="App">
        <Header />
        <LoginPage />
        <Footer />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="App">
        <Header />
        <div className="container py-4">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading configuration...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Header />
        <div className="container py-4">
          <div className="alert alert-danger">
            <h4>Configuration Error</h4>
            <p>{error}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const renderActiveModule = () => {
    if (!activeModule) {
      return (
        <div className="container py-4">
          <div className="alert alert-warning">
            <h4>No Active Module</h4>
            <p>No module is currently active or configured.</p>
          </div>
        </div>
      );
    }

    switch (activeModule.id) {
      case 'custom-mats':
        return <CustomMatsPage />;
      case 'car-mats':
        return <ProductsPage productType="car_mat" />;
      case 'trunk-mats':
        return <ProductsPage productType="trunk_mat" />;
      case 'other-products':
        return <ProductsPage productType="other" />;
      case 'products':
        return <ProductsPage />;
      case 'uv-kits':
        return <UVKitsPage />;
      default:
        return (
          <div className="container py-4">
            <div className="alert alert-warning">
              <h4>Module Not Found</h4>
              <p>The module "{activeModule.id}" is not implemented yet.</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="App">
      <Header />
      <Routes>
        <Route path="/account" element={<UserAccountPage />} />
        <Route path="/orders" element={<OrderHistoryPage />} />
        <Route path="/contact" element={<ContentPage contentKey="content.contact" />} />
        <Route path="/help" element={<ContentPage contentKey="content.help" />} />
        <Route path="/terms" element={<ContentPage contentKey="content.terms" />} />
        <Route path="/" element={permissions.canAccessProducts ? renderActiveModule() : <Navigate to="/account" replace />} />
      </Routes>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ModuleConfigProvider>
          <OrderLimitsProvider>
            <CartProvider>
              <SelectionProvider>
                <AppContent />
              </SelectionProvider>
            </CartProvider>
          </OrderLimitsProvider>
        </ModuleConfigProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;